import {Component, ElementRef, Input, OnDestroy, OnInit, QueryList, ViewChildren} from '@angular/core';
import {QuizService} from "../services/quiz.service";
import {Quiz} from "../models/quiz";
import {AuthService} from "../services/auth.service";
import {GeneralRequestModel} from "../models/generalRequestModel";
import {QuizStatus} from "../models/quizStatus";
import {environment} from "../../environments/environment";
import {MatDialog} from "@angular/material/dialog";
import {QuizDialogComponent} from "../quiz/quiz-dialog/quiz-dialog.component";
import {SharedService} from "../services/shered.service";
import {DomSanitizer, SafeHtml} from "@angular/platform-browser";
import {ApiResponse} from "../models/ApiResponse";
import {QuizStatusCheckService} from "../services/quiz.status.check.service";
import {ScreenSizeService} from "../services/screen-size.service";
import {FormControl} from "@angular/forms";
import {Subject, Subscription} from "rxjs";

@Component({
  selector: 'app-shared',
  templateUrl: './shared.component.html',
  styleUrls: ['./shared.component.css']
})
export class SharedComponent implements OnInit, OnDestroy {

  @Input() isMobileMenuClosed!: boolean;
  @Input() isMobile!: boolean;

  quizzes: Array<Quiz> = [];
  searchResults: Array<Quiz> = [];
  pageNumber: number = 1;
  pageSize: number = 10;
  quizzesTotalCount: number = 0;
  apiUrl!: string;
  searchTerm: string = '';
  maxVisiblePages: number = 3;
  isSelectResultsOpened: boolean = false;
  searchFormControl: FormControl<string> = new FormControl();
  selectedResult!: string;
  showSearchMobile: boolean = false;
  currentUserId: string | null = null;

  private readonly searchSubject: Subject<string> = new Subject<string>();
  private readonly subscriptions: Array<Subscription> = [];

  @ViewChildren('dropdownMenu') dropdownMenus!: QueryList<ElementRef>;

  constructor(
    private readonly authService: AuthService,
    private readonly quizService: QuizService,
    private readonly sharedService: SharedService,
    private readonly sanitizer: DomSanitizer,
    private readonly quizStatusCheckService: QuizStatusCheckService,
    private readonly screenSizeService: ScreenSizeService,
    private readonly dialog: MatDialog,
  ) {
    this.currentUserId = this.authService.getCurrentUserId();
  }

  ngOnInit(): void {
    let onUpdatedEventSubscription = this.subscribeOnUpdatedEvent();

    this.getQuizzes(this.pageNumber, this.pageSize);
    this.apiUrl = environment.apiUrl;
    let isMobileSubscription = this.initIsMobileSubscription();
    let searchValueChangesSubscription = this.subscribeToSearchValueChanges();

    this.subscriptions.push(onUpdatedEventSubscription, isMobileSubscription, searchValueChangesSubscription);
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription: Subscription) => {
      subscription.unsubscribe();
    })
  }

  onPageChange(page: number) {
    this.pageNumber = page;
    this.handleResultRetrieval();
  }

  getStatus(enumType: string): string {
    switch (enumType) {
      case QuizStatus.SETUP_IN_PROGRESS:
        return 'Setup in process';
      case QuizStatus.ACTIVE:
        return 'Active';
      case QuizStatus.ENDED:
        return 'Ended';
      default:
        return 'Unknown type';
    }
  }

  getSanitizedHTML(htmlContent: string | undefined): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(<string>htmlContent);
  }

  openDuplicateDialog(quizName: string | undefined, quizId: string | undefined): void {
    this.dialog.open(QuizDialogComponent, {
      width: '510px',
      data: {
        showDuplicateDialog: true,
        quizName: quizName,
        quizId: quizId,
        userId: this.currentUserId
      }
    });
  }

  openDeleteDialog(quizId: string | undefined) {
    this.dialog.open(QuizDialogComponent, {
      width: '510px',
      data: {
        showDeleteDialog: true,
        quizId: quizId,
        userId: this.currentUserId
      }
    });
  }

  onSearch(searchTerm: string, page: number, count: number) {
    this.searchTerm = searchTerm;
    if (searchTerm.length >= 2) {
      this.pageNumber = page;
      this.pageSize = count;
      this.quizService.searchQuiz(
        searchTerm.trim(),
        this.currentUserId,
        {page: this.pageNumber, count: this.pageSize}
      ).subscribe({
        next: (quizApiResponse: ApiResponse<Quiz>) => {
          this.initializeProperties(quizApiResponse);
        }
      });
    } else if (searchTerm === '') {
      this.getQuizzes(this.pageNumber, this.pageSize);
    }
  }

  handleSearch(searchTerm: string, page: number, count: number): void {
    if (searchTerm.length >= 1) {
      this.showSearchMobile = true;
      this.quizService.search(searchTerm, this.currentUserId, {page, count})
        .subscribe({
          next: (searchResponse: ApiResponse<Quiz>) => {
            this.initializeProperties(searchResponse)
          }
        });
      this.isSelectResultsOpened = this.searchResults.length > 0;
    } else if (searchTerm === '') {
      this.getQuizzes(page, count);
      this.isSelectResultsOpened = false;
      this.showSearchMobile = false;
    }
  }

  selectTest(searchTerm: string): void {
    this.handleSearch(searchTerm, this.pageNumber, this.pageSize);
    this.selectedResult = searchTerm;
    this.isSelectResultsOpened = false;
    this.showSearchMobile = false;
  }

  canDelete(quizStatus: QuizStatus): boolean {
    return this.quizStatusCheckService.canDelete(quizStatus);
  }

  canEdit(quizStatus: QuizStatus): boolean {
    return this.quizStatusCheckService.isReadOnly(quizStatus);
  }

  subscribeToSearchValueChanges(): Subscription {
    return this.searchFormControl.valueChanges.subscribe({
      next: (value: string) => {
        this.isSelectResultsOpened = true;
        this.searchSubject.next(value);
      }
    })
  }

  splitStatusForMobile(status: QuizStatus.SETUP_IN_PROGRESS): string {
    return status.split('_')[0].toLowerCase();
  }

  private initIsMobileSubscription(): Subscription {
    return this.screenSizeService.isMobile$.subscribe({
        next: (isMobile: boolean) => {
          this.isMobile = isMobile;
          this.isMobileMenuClosed = isMobile;
        }
      }
    );
  }

  private subscribeOnUpdatedEvent(): Subscription {
    return this.sharedService.updatedEvent.subscribe({
      next() {
        this.pageNumber = 1;
        this.pageSize = 10;
        this.handleResultRetrieval();
      }
    });
  }

  private initializeProperties(apiResponse: ApiResponse<Quiz>): void {
    this.quizzes = apiResponse.data.map(quiz => ({
      ...quiz,
      readOnly: this.quizStatusCheckService.isReadOnly(quiz.status)
    }));
    this.searchResults = this.quizzes;
    this.quizzesTotalCount = apiResponse.meta.total;
  }

  private handleResultRetrieval(): void {
    if (this.searchTerm.trim().length > 2) {
      this.onSearch(this.searchTerm, this.pageNumber, this.pageSize);
    } else {
      this.getQuizzes(this.pageNumber, this.pageSize);
    }
  }

 private  getQuizzes(page: number, count: number): void {
    const generalRequest: GeneralRequestModel = {page, count};
    this.quizService.getAllByUserId(this.currentUserId, generalRequest).subscribe({
        next: (allQuiz: ApiResponse<Quiz>) => {
          this.initializeProperties(allQuiz);
        },
      }
    );
  }

  protected readonly QuizStatus: typeof QuizStatus = QuizStatus;
  protected readonly Math = Math;
}
